// root '../../../';

// dependencies
import { Child } from 'pym.js';
import { select } from 'd3-selection';
import debounce from 'lodash/fp/debounce';
import accessibleAutocomplete from 'accessible-autocomplete';
import * as d3 from 'd3';
import Zion from './utils/Zion';


// polyfills
// import fetch from 'unfetch';
import './utils/polyfills'; // (includes Array.findIndex && Array.find)

// utils & graphic
import config from './config';
import graphic from './graphic';

// styles
import './styles/index.scss';

/* develblock:start */
import meta from './data/meta.json';

const pkg = require('../package.json');

if (process.env.NODE_ENV === 'development') {
  // console.clear();
}

// Function to convert date string "YYYY-MM-DD" to "Month, YYYY" format
const formatDate = (dateString) => {
  const date = new Date(`${dateString}T00:00:00Z`); // Use UTC to avoid timezone issues
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
  ];
  const month = months[date.getUTCMonth()];
  const year = date.getUTCFullYear();
  return `${month}`;
};


const defaultPlaces = [
  'Chicago-Naperville-Elgin, IL-IN',
  'Los Angeles-Long Beach-Anaheim, CA',
  'New York-Newark-Jersey City, NY-NJ',
];


// purple #7C4EA5
// blue #5194C3
// oragne #F18851
// green #53A796
// yellow #F8C153
const palette = ['#5194C3', '#53A796', '#F18851', '#7C4EA5'];
let placeToColor = new Map();

function assignColors(selectedPlaces, lookup) {
  // Get all colors currently assigned
  const assignedColors = new Set([...placeToColor.values()]);

  const places = selectedPlaces.map(
    d => d,
  );


  // Remove places no longer selected, freeing colors
  for (const place of placeToColor.keys()) {
    if (!places.has(place)) {
      placeToColor.delete(place);
    }
  }

  // Assign colors to new places
  for (const place of places) {
    if (!placeToColor.has(place)) {
      // Find first free color
      const freeColor = palette.find(c => !assignedColors.has(c));
      if (freeColor) {
        placeToColor.set(place, freeColor);
        assignedColors.add(freeColor);
      } else {
        // All colors taken, assign fallback (or cycle)
        placeToColor.set(place, '#999999');
      }
    }
  }
}

const setInputDisabled = () => {
  const inputBox = document.getElementById('metro-autocomplete');
  if (selectedPlaces.size > 3) {
    inputBox.setAttribute('disabled', true);
  } else {
    inputBox.removeAttribute('disabled');
  }
};


let hasInit;
let jsonData;
let selectedPlaces = new Set(defaultPlaces);

let hasSearched = true;
const endpoint = 'https://ix.cnn.io/data/housing-affordability/cbsa_housing_affordability_index_history.json';


let lookup = {};


const init = async (graphicConfig, container, isMobile) => {
  if (!hasInit) {
    hasInit = true;
    const response = await fetch(endpoint);
    jsonData = await response.json();

    lookup = jsonData.data.metros.reduce(
      (acc, cur) => {
        acc[cur.name] = cur.place;
        return acc;
      },
      {},
    );

    assignColors(selectedPlaces, lookup);

    // Format the latest_date and combine with lastUpdatedStr
    const formattedDate = formatDate(jsonData.latest_date);
    select('#timestamp').text(`Data current through ${formattedDate} as of ${jsonData.lastUpdatedStr}.`);


    graphic(graphicConfig, container, isMobile, jsonData, 'last_score', selectedPlaces, placeToColor);

    // search setup
    const places = [...new Set(jsonData.data.metros.map(d => d.name))];

    accessibleAutocomplete({
      element: document.querySelector('#metro-autocomplete-container'),
      id: 'metro-autocomplete', // To match it to the existing <label>.
      placeholder: 'Search for up to four metro areas...',
      displayMenu: 'overlay',
      source: places,
      onConfirm: (val) => {
        if (!val) return;
        if (!hasSearched) {
          selectedPlaces = new Set([val]);
          hasSearched = true;
        } else {
          selectedPlaces = selectedPlaces.add(val);
        }
        assignColors(selectedPlaces, lookup);

        const inputBox = document.getElementById('metro-autocomplete');
        inputBox.value = '';
        setTimeout(() => document.getElementById('metro-autocomplete').value = '', 100);

        setInputDisabled();

        // searchResults.html('');
        graphic(null, null, null, jsonData, null, selectedPlaces, placeToColor);
      },
    });

    const searchBox = d3.select('#searchBox');
    const clearButton = d3.select('#clearButton');
    clearButton.on('click', () => {
      // searchBox.property('value', '');

      selectedPlaces = new Set([]);
      placeToColor = new Map();
      setInputDisabled();
      graphic(null, null, null, jsonData, null, selectedPlaces, placeToColor);
      document.getElementById('metro-autocomplete').value = '';
      // filterAndDraw(processedData);
    });


    document.querySelector('#graphic').addEventListener('update-selected-places', (e) => {
      if (e.detail && e.detail.removePlace && selectedPlaces.has(e.detail.removePlace)) {
        selectedPlaces.delete(e.detail.removePlace);
        graphic(null, null, null, jsonData, null, selectedPlaces, placeToColor);
        setInputDisabled();
      }
      // selectedPlaces = new Set(e.detail.selectedPlaces);
      // graphic(null, null, null, jsonData, null, selectedPlaces);
    });
  } else if (jsonData) {
    graphic(graphicConfig, container, isMobile, jsonData, null, selectedPlaces, placeToColor);
  }
};


const draw = async (initialWidth) => {
  const container = select('#graphic');
  // container.html('');

  const fallbackWidth = container.node().clientWidth || 600;
  const safeWidth = initialWidth !== fallbackWidth ? fallbackWidth : initialWidth;
  const isMobile = safeWidth <= 500;

  const heightMultiplier = isMobile ? 1 : 9 / 16;
  const initialHeight = heightMultiplier * safeWidth;

  const props = { initialWidth: safeWidth, initialHeight, isMobile };

  const graphicConfig = config(props);
  // graphic(graphicConfig, container, isMobile);
  // hasInit = true;
  init(graphicConfig, container, isMobile);
};


const debouncedDraw = debounce(100)(draw);

// eslint-disable-next-line  no-use-before-define
window.pymChild = new Child({ renderCallback: debouncedDraw, id: pkg.name });

window.onload = () => {
  // necessary evil for right now

  if (process.env.NODE_ENV === 'development') {
    Object.keys(meta).forEach((id) => {
      const node = document.querySelector(`.${id}`);

      if (node) {
        node.innerHTML = meta[id];
      }
    });
  }

  const { pymChild } = window;
  pymChild.sendHeight();

  // If embedded, send Zion page view event
  if (window.pymChild.parentUrl) {
    const zion = new Zion(pkg.name, pymChild.parentUrl.split('?')[0]);
  }
};
